import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  buildSurveyTeamSlotIso,
  parseSurveyTeamScheduledSlots,
  strapiCompleteScheduleAdminSampling,
  strapiCompleteScheduleAdminWorkorder,
  strapiConnectSamplingPlanToSchedule,
  strapiCreateSurveyTeam,
  strapiDeleteSurveyTeam,
  strapiEnsureSamplingScheduleForQuotation,
  strapiApproveSamplingSchedule,
  strapiGetSamplingSchedule,
  strapiListAllSamplingSchedulesForCalendar,
  strapiPromoteScheduleToWorkOrder,
  strapiRemoveScheduledDateFromSurveyTeams,
  strapiResolveSurveyTeamsForSamplingSchedule,
  strapiUpdateSamplingSchedule,
  strapiUpdateWorkOrder,
} from '../../api/strapiCrm';
import { freelanceMemberKey } from '../../api/strapiFreelanceUsers';
import { strapiUserRelationConnect } from '../../api/strapiUsers';
import { useAuth } from '../../auth/AuthContext';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageShell } from '../../layout/PageShell';
import { useNavigationMenuQuery, useUsersQuery } from '../../query/hooks';
import { queryKeys } from '../../query/queryKeys';
import { canAccessSamplingSchedulesPage, quotationUsesSampleGroups } from '../QuotationsPage/quotationWorkflow';
import {
  canApproveSurveyTeamForSampling,
  canEditScheduleTransportasiNote,
  isAdminSampling,
  isAdminWorkOrder,
  isSuperadminType,
  samplingScheduleDetailLocked,
} from './samplingScheduleAccess';
import {
  buildCustomerAgreedDatePayload,
  customerAgreedCalendarDatesFromSchedule,
} from './samplingScheduleCalendarModel';
import {
  SamplingSchedulePlanningTab,
  type ScheduleAppendRoster,
  type ScheduleTeamMemberDraft,
} from './TabScreens/Team';
import type { SlotAssignWorkOrderPayload } from '../../components/surveyScheduleCalendar';

function customerAgreedDatesFromSlots(slotsByUserId: Map<string, string[]>): ReturnType<
  typeof buildCustomerAgreedDatePayload
> {
  const dates = new Set<string>();
  for (const slots of slotsByUserId.values()) {
    for (const parsed of parseSurveyTeamScheduledSlots(slots)) {
      dates.add(parsed.date);
    }
  }
  return buildCustomerAgreedDatePayload(dates);
}

function asSlotsMap(
  slotsByUserId: Map<string, string[]> | Record<string, string[]> | null | undefined,
): Map<string, string[]> {
  if (slotsByUserId instanceof Map) {
    return new Map(
      [...slotsByUserId.entries()].map(([key, value]) => [String(key), [...(value ?? [])]]),
    );
  }
  if (slotsByUserId && typeof slotsByUserId === 'object') {
    return new Map(
      Object.entries(slotsByUserId).map(([key, value]) => [String(key), [...(value ?? [])]]),
    );
  }
  return new Map();
}

export function SamplingScheduleDetailRoute() {
  const { documentId } = useParams<{ documentId: string }>();
  const { jwt, user } = useAuth();
  const menuQ = useNavigationMenuQuery(jwt);
  const canView = canAccessSamplingSchedulesPage(user, menuQ.data?.access_codes);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [teamSaveSuccessOpen, setTeamSaveSuccessOpen] = useState(false);
  const [redirectToListAfterSuccess, setRedirectToListAfterSuccess] = useState(false);
  const [successWasDateOnly, setSuccessWasDateOnly] = useState(false);
  const [successCreatedWorkOrder, setSuccessCreatedWorkOrder] = useState(false);
  const [successNeedsTeamApproval, setSuccessNeedsTeamApproval] = useState(false);
  const [teamSaveTick, setTeamSaveTick] = useState(0);

  useDocumentTitle(appDocumentTitle('Penjadwalan Sampling'));

  const detailQ = useQuery({
    queryKey: ['strapi', 'samplingSchedule', jwt, documentId],
    queryFn: () => strapiGetSamplingSchedule(jwt!, documentId!),
    enabled: Boolean(jwt && documentId),
  });

  const scheduleDetail = detailQ.data;

  const teamsQ = useQuery({
    queryKey: [
      'strapi',
      'surveyTeamsBySchedule',
      jwt,
      documentId,
      scheduleDetail?.work_order?.documentId ?? '',
      scheduleDetail?.schedule_kind ?? 'initial',
    ],
    queryFn: () => {
      const kind = scheduleDetail?.schedule_kind ?? 'initial';
      return strapiResolveSurveyTeamsForSamplingSchedule(
        jwt!,
        documentId!,
        scheduleDetail?.work_order?.documentId ?? null,
        // point_reschedule shares the WO with the initial schedule — never fall back to WO teams.
        { allowWorkOrderFallback: kind !== 'point_reschedule' },
      );
    },
    enabled: Boolean(jwt && documentId && scheduleDetail),
  });

  const usersQ = useUsersQuery(jwt, { emailContains: '', status: 'active' });

  const schedulesForCalendarQ = useQuery({
    queryKey: ['strapi', 'samplingSchedulesForCalendarOverview', jwt],
    queryFn: () => strapiListAllSamplingSchedulesForCalendar(jwt!),
    enabled: Boolean(jwt),
  });

  const schedule = scheduleDetail;
  const schedulesForCalendar = useMemo(() => {
    const rows = schedulesForCalendarQ.data ?? [];
    if (!schedule) return rows;
    const idx = rows.findIndex((row) => row.documentId === schedule.documentId);
    if (idx < 0) return [...rows, schedule];
    const next = [...rows];
    next[idx] = schedule;
    return next;
  }, [schedulesForCalendarQ.data, schedule]);
  const locked = schedule ? samplingScheduleDetailLocked(schedule, user) : true;
  const teamAgreedDates = customerAgreedCalendarDatesFromSchedule(schedule);
  const teamAnchorDate = teamAgreedDates[0] ?? null;
  const teamAnchorEndDate = teamAgreedDates.length
    ? teamAgreedDates[teamAgreedDates.length - 1]
    : null;
  const status = schedule?.progress_status ?? '';
  const woDocId = schedule?.work_order?.documentId;
  const hasScheduledDate = teamAgreedDates.length > 0;
  const isWo = isAdminWorkOrder(user);
  const isSamplingAdmin = isAdminSampling(user);
  const isSuper = isSuperadminType(user);
  const isPointReschedule = (schedule?.schedule_kind ?? 'initial') === 'point_reschedule';
  const editMode = locked
    ? 'readonly'
    : isSuper
      ? hasScheduledDate || isPointReschedule
        ? 'full'
        : 'date_only'
      : isSamplingAdmin
        ? 'full'
        : isWo && !isPointReschedule
          ? 'date_only'
          : 'readonly';
  const usesGroups = schedule?.quotation ? quotationUsesSampleGroups(schedule.quotation) : false;

  const saveTeamMut = useMutation({
    mutationFn: async ({
      members,
      slotsByUserId: rawSlotsByUserId,
      notes,
      planDocumentIds,
      dateOnlySlots,
      transportasiNote,
      allowEmptyTeam = false,
      quotationGroupDocumentId,
      quotationRecurringEventDocumentId,
      targetScheduleDocumentId,
      workOrderCreate,
      teamNamesByDate,
      teamName,
      propagateRosterToScheduleDocumentIds,
      replaceRosterOnDate,
      appendRoster,
    }: {
      members: ScheduleTeamMemberDraft[];
      slotsByUserId: Map<string, string[]> | Record<string, string[]>;
      notes: string;
      planDocumentIds?: string[];
      dateOnlySlots?: string[];
      transportasiNote?: string;
      stayOnPage?: boolean;
      allowEmptyTeam?: boolean;
      quotationGroupDocumentId?: string;
      quotationRecurringEventDocumentId?: string;
      targetScheduleDocumentId?: string;
      workOrderCreate?: SlotAssignWorkOrderPayload;
      teamNamesByDate?: Record<string, string> | null;
      /** @deprecated Gunakan teamNamesByDate */
      teamName?: string | null;
      propagateRosterToScheduleDocumentIds?: string[];
      /** Ganti petugas hanya pada tanggal ini. Jadwal tanggal lain tidak dihapus. */
      replaceRosterOnDate?: string;
      /** Tambah petugas pada tanggal kosong. Baris petugas yang sudah ada tidak dihapus. */
      appendRoster?: ScheduleAppendRoster | null;
    }) => {
      if (!jwt || !documentId) throw new Error('Sesi tidak valid.');
      const legacyTeamName = String(teamName ?? '').trim() || null;
      const teamNameForDate = (date: string): string | null => {
        const fromMap = String(teamNamesByDate?.[date] ?? '').trim();
        if (fromMap) return fromMap;
        return legacyTeamName;
      };

      const selectedGroupId = String(quotationGroupDocumentId ?? '').trim();
      const selectedEventId = String(quotationRecurringEventDocumentId ?? '').trim();
      const quotationDocId = String(schedule?.quotation?.documentId ?? '').trim();
      const explicitTarget = String(targetScheduleDocumentId ?? '').trim();
      let targetDocumentId = explicitTarget || documentId;
      if (!explicitTarget && usesGroups && selectedGroupId && quotationDocId) {
        const ensured = await strapiEnsureSamplingScheduleForQuotation(
          jwt,
          quotationDocId,
          selectedGroupId,
          selectedEventId || undefined,
        );
        if (!ensured.documentId) throw new Error('Gagal membuat penjadwalan untuk pelanggan ini.');
        targetDocumentId = ensured.documentId;
      }
      const switchingSchedule = targetDocumentId !== documentId;
      const targetRow =
        schedulesForCalendar.find((row) => row.documentId === targetDocumentId) ??
        (switchingSchedule ? null : schedule);
      const targetWoDocId = String(
        targetRow?.work_order?.documentId ??
          (!switchingSchedule ? schedule?.work_order?.documentId : '') ??
          '',
      ).trim();
      const targetWoLinked = Boolean(targetWoDocId);

      const rosterDate = String(replaceRosterOnDate ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(rosterDate)) {
        if (members.length === 0) {
          throw new Error('Gagal menyimpan tim: data petugas tidak lengkap.');
        }
        const slotsByUserId = asSlotsMap(rawSlotsByUserId);
        await strapiRemoveScheduledDateFromSurveyTeams({
          jwt,
          scheduleDocumentId: targetDocumentId,
          date: rosterDate,
          workOrderDocumentId: targetWoDocId || null,
        });
        const nameForDate = String(teamNamesByDate?.[rosterDate] ?? '').trim() || null;
        for (const row of members) {
          if (row.user) {
            const slots = (slotsByUserId.get(String(row.user.id)) ?? []).filter((rawSlot) => {
              const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
              return parsed?.date === rosterDate;
            });
            for (const rawSlot of slots) {
              await strapiCreateSurveyTeam(jwt, {
                sampling_schedule: { connect: [targetDocumentId] },
                ...(targetWoDocId ? { work_order: { connect: [targetWoDocId] } } : {}),
                user_idx: strapiUserRelationConnect(row.user),
                is_lead: row.is_lead,
                scheduled_date: [rawSlot],
                ...(nameForDate ? { team_name: nameForDate } : { team_name: null }),
              });
            }
            continue;
          }
          const freelanceDocId = row.freelanceUser?.documentId?.trim();
          if (!freelanceDocId) continue;
          const slots = (slotsByUserId.get(freelanceMemberKey(freelanceDocId)) ?? []).filter(
            (rawSlot) => {
              const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
              return parsed?.date === rosterDate;
            },
          );
          for (const rawSlot of slots) {
            await strapiCreateSurveyTeam(jwt, {
              sampling_schedule: { connect: [targetDocumentId] },
              ...(targetWoDocId ? { work_order: { connect: [targetWoDocId] } } : {}),
              freelance_user: { connect: [freelanceDocId] },
              is_lead: row.is_lead,
              scheduled_date: [rawSlot],
              ...(nameForDate ? { team_name: nameForDate } : { team_name: null }),
            });
          }
        }
        return {
          isDateOnlySave: false,
          targetDocumentId,
          switchingSchedule,
          createdWorkOrderDocumentId: null,
        };
      }

      const slotsByUserId = asSlotsMap(rawSlotsByUserId);
      const hasAssignedSlots = [...slotsByUserId.values()].some((list) => (list?.length ?? 0) > 0);
      const isDateOnlySave =
        members.length === 0 &&
        !hasAssignedSlots &&
        Array.isArray(dateOnlySlots) &&
        dateOnlySlots.length > 0;
      if (members.length === 0 && hasAssignedSlots) {
        throw new Error('Gagal menyimpan tim: data petugas tidak lengkap.');
      }
      if (!isDateOnlySave) {
        const activeMemberKeys = members
          .map((row) => {
            if (row.user?.id && (slotsByUserId.get(String(row.user.id))?.length ?? 0) > 0) {
              return String(row.user.id);
            }
            const freelanceDocId = row.freelanceUser?.documentId?.trim();
            if (freelanceDocId) {
              const key = freelanceMemberKey(freelanceDocId);
              if ((slotsByUserId.get(key)?.length ?? 0) > 0) return key;
            }
            return null;
          })
          .filter((key): key is string => Boolean(key));
        const uniqueMemberCount = new Set(activeMemberKeys).size;
        if (!allowEmptyTeam && uniqueMemberCount < 2) {
          throw new Error('Tim sampling minimal harus terdiri dari 2 petugas.');
        }

        // Selalu fetch fresh by schedule (tanpa WO fallback) agar hapus/replace akurat.
        const existingTeams = await strapiResolveSurveyTeamsForSamplingSchedule(
          jwt,
          targetDocumentId,
          null,
          { allowWorkOrderFallback: false },
        );

        for (const existing of existingTeams) {
          if (!existing.documentId) continue;
          await strapiDeleteSurveyTeam(jwt, existing.documentId);
        }
        for (const row of members) {
          if (row.user) {
            const slots = slotsByUserId.get(String(row.user.id)) ?? [];
            if (!slots.length) continue;
            for (const rawSlot of slots) {
              const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
              if (!parsed) continue;
              const nameForDate = teamNameForDate(parsed.date);
              await strapiCreateSurveyTeam(jwt, {
                sampling_schedule: { connect: [targetDocumentId] },
                ...(targetWoDocId ? { work_order: { connect: [targetWoDocId] } } : {}),
                user_idx: strapiUserRelationConnect(row.user),
                is_lead: row.is_lead,
                scheduled_date: [rawSlot],
                ...(nameForDate ? { team_name: nameForDate } : { team_name: null }),
              });
            }
            continue;
          }
          const freelanceDocId = row.freelanceUser?.documentId?.trim();
          if (!freelanceDocId) continue;
          const slots = slotsByUserId.get(freelanceMemberKey(freelanceDocId)) ?? [];
          if (!slots.length) continue;
          for (const rawSlot of slots) {
            const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
            if (!parsed) continue;
            const nameForDate = teamNameForDate(parsed.date);
            await strapiCreateSurveyTeam(jwt, {
              sampling_schedule: { connect: [targetDocumentId] },
              ...(targetWoDocId ? { work_order: { connect: [targetWoDocId] } } : {}),
              freelance_user: { connect: [freelanceDocId] },
              is_lead: row.is_lead,
              scheduled_date: [rawSlot],
              ...(nameForDate ? { team_name: nameForDate } : { team_name: null }),
            });
          }
        }

        // Jadwal lain di card yang sama: ganti roster, pertahankan tanggal masing-masing.
        const propagateIds = [
          ...new Set(
            (propagateRosterToScheduleDocumentIds ?? [])
              .map((id) => String(id ?? '').trim())
              .filter((id) => id && id !== targetDocumentId),
          ),
        ];
        for (const siblingId of propagateIds) {
          const siblingRow =
            schedulesForCalendar.find((row) => row.documentId === siblingId) ?? null;
          const siblingWoDocId = String(siblingRow?.work_order?.documentId ?? '').trim();
          const siblingTeams = await strapiResolveSurveyTeamsForSamplingSchedule(
            jwt,
            siblingId,
            null,
            { allowWorkOrderFallback: false },
          );
          const siblingSlotByDate = new Map<string, string>();
          const siblingTeamNameByDate = new Map<string, string>();
          for (const team of siblingTeams) {
            const siblingName = String(team.team_name ?? '').trim();
            for (const parsed of parseSurveyTeamScheduledSlots(team.scheduled_date)) {
              if (!siblingSlotByDate.has(parsed.date)) {
                siblingSlotByDate.set(parsed.date, parsed.raw);
              }
              if (siblingName && !siblingTeamNameByDate.has(parsed.date)) {
                siblingTeamNameByDate.set(parsed.date, siblingName);
              }
            }
          }
          const siblingSharedSlots = [...siblingSlotByDate.entries()]
            .sort(([a], [b]) => a.localeCompare(b, 'id'))
            .map(([, raw]) => raw);
          if (siblingSharedSlots.length === 0) continue;

          for (const existing of siblingTeams) {
            if (!existing.documentId) continue;
            await strapiDeleteSurveyTeam(jwt, existing.documentId);
          }
          for (const row of members) {
            if (row.user) {
              for (const rawSlot of siblingSharedSlots) {
                const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
                if (!parsed) continue;
                const nameForDate = siblingTeamNameByDate.get(parsed.date) ?? null;
                await strapiCreateSurveyTeam(jwt, {
                  sampling_schedule: { connect: [siblingId] },
                  ...(siblingWoDocId ? { work_order: { connect: [siblingWoDocId] } } : {}),
                  user_idx: strapiUserRelationConnect(row.user),
                  is_lead: row.is_lead,
                  scheduled_date: [rawSlot],
                  ...(nameForDate ? { team_name: nameForDate } : { team_name: null }),
                });
              }
              continue;
            }
            const freelanceDocId = row.freelanceUser?.documentId?.trim();
            if (!freelanceDocId) continue;
            for (const rawSlot of siblingSharedSlots) {
              const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
              if (!parsed) continue;
              const nameForDate = siblingTeamNameByDate.get(parsed.date) ?? null;
              await strapiCreateSurveyTeam(jwt, {
                sampling_schedule: { connect: [siblingId] },
                ...(siblingWoDocId ? { work_order: { connect: [siblingWoDocId] } } : {}),
                freelance_user: { connect: [freelanceDocId] },
                is_lead: row.is_lead,
                scheduled_date: [rawSlot],
                ...(nameForDate ? { team_name: nameForDate } : { team_name: null }),
              });
            }
          }
        }
      }
      const customerDates = isDateOnlySave
        ? buildCustomerAgreedDatePayload(dateOnlySlots ?? [])
        : buildCustomerAgreedDatePayload([
            ...customerAgreedDatesFromSlots(slotsByUserId).customer_agreed_dates,
            ...(Array.isArray(dateOnlySlots) ? dateOnlySlots : []),
          ]);
      await strapiUpdateSamplingSchedule(jwt, targetDocumentId, {
        ...(targetWoLinked ? {} : { progress_status: 'scheduling_in_progress' }),
        notes: notes.trim() || null,
        ...(canEditScheduleTransportasiNote(user)
          ? { transportasi_note: (transportasiNote ?? '').trim() || null }
          : {}),
        customer_agreed_dates: customerDates.customer_agreed_dates,
        customer_agreed_start_date: customerDates.customer_agreed_start_date,
        customer_agreed_end_date: customerDates.customer_agreed_end_date,
        ...(selectedGroupId ? { quotation_group: { connect: [selectedGroupId] } } : {}),
        ...(selectedEventId
          ? { quotation_recurring_event: { connect: [selectedEventId] } }
          : {}),
      });
      if (targetWoLinked && targetWoDocId) {
        await strapiUpdateWorkOrder(jwt, targetWoDocId, {
          testing_start_date: customerDates.customer_agreed_start_date,
          testing_end_date: customerDates.customer_agreed_end_date,
        });
      }
      if (isDateOnlySave && appendRoster && appendRoster.dates.length > 0 && appendRoster.members.length > 0) {
        const copiedTeamName = String(appendRoster.teamName ?? '').trim() || null;
        for (const date of appendRoster.dates) {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
          for (const member of appendRoster.members) {
            const time = /^\d{2}:\d{2}$/.test(member.time) ? member.time : '08:00';
            let rawSlot: string;
            try {
              rawSlot = buildSurveyTeamSlotIso(date, time);
            } catch {
              continue;
            }
            const teamNamePayload = copiedTeamName ? { team_name: copiedTeamName } : { team_name: null };
            if (member.user) {
              await strapiCreateSurveyTeam(jwt, {
                sampling_schedule: { connect: [targetDocumentId] },
                ...(targetWoDocId ? { work_order: { connect: [targetWoDocId] } } : {}),
                user_idx: strapiUserRelationConnect(member.user),
                is_lead: member.isLead,
                scheduled_date: [rawSlot],
                ...teamNamePayload,
              });
              continue;
            }
            const freelanceDocId = member.freelanceUser?.documentId?.trim();
            if (!freelanceDocId) continue;
            await strapiCreateSurveyTeam(jwt, {
              sampling_schedule: { connect: [targetDocumentId] },
              ...(targetWoDocId ? { work_order: { connect: [targetWoDocId] } } : {}),
              freelance_user: { connect: [freelanceDocId] },
              is_lead: member.isLead,
              scheduled_date: [rawSlot],
              ...teamNamePayload,
            });
          }
        }
      }
      if (planDocumentIds?.length) {
        for (const planId of planDocumentIds) {
          await strapiConnectSamplingPlanToSchedule(jwt, targetDocumentId, planId);
        }
      }
      if (!targetWoLinked && (isWo || (isSuper && isDateOnlySave))) {
        await strapiCompleteScheduleAdminWorkorder(jwt, targetDocumentId);
      }
      const targetIsPointReschedule =
        (targetRow?.schedule_kind ??
          (switchingSchedule ? 'initial' : schedule?.schedule_kind) ??
          'initial') === 'point_reschedule';
      if (
        !isDateOnlySave &&
        targetIsPointReschedule &&
        (isSamplingAdmin || isSuper)
      ) {
        await strapiCompleteScheduleAdminSampling(jwt, targetDocumentId);
      }
      let createdWorkOrderDocumentId: string | null = null;
      if (workOrderCreate && !targetWoLinked) {
        const wo = await strapiPromoteScheduleToWorkOrder(jwt, targetDocumentId, {
          work_order_date: workOrderCreate.work_order_date,
          progress_status: workOrderCreate.progress_status,
          roman_quarter: workOrderCreate.roman_quarter,
          calendar_year: workOrderCreate.calendar_year,
        });
        createdWorkOrderDocumentId = wo.documentId || null;
      }
      return { isDateOnlySave, targetDocumentId, switchingSchedule, createdWorkOrderDocumentId };
    },
    onSuccess: async (data, variables) => {
      const qDocId = detailQ.data?.quotation?.documentId;
      await Promise.all([
        teamsQ.refetch(),
        qc.refetchQueries({ queryKey: queryKeys.surveyTeamsSchedule(jwt, null) }),
        qc.refetchQueries({ queryKey: ['strapi', 'samplingSchedule', jwt, documentId] }),
        qc.refetchQueries({
          queryKey: ['strapi', 'samplingSchedulesForCalendarOverview', jwt],
        }),
        data.targetDocumentId && data.targetDocumentId !== documentId
          ? qc.refetchQueries({
              queryKey: ['strapi', 'samplingSchedule', jwt, data.targetDocumentId],
            })
          : Promise.resolve(),
        qDocId
          ? Promise.all([
              qc.invalidateQueries({
                queryKey: ['strapi', 'samplingPlansForQuotation', jwt, qDocId],
              }),
              qc.invalidateQueries({
                queryKey: ['strapi', 'ensureGroupedSchedulePlans', jwt, qDocId],
              }),
              qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, qDocId) }),
              qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) }),
            ])
          : Promise.resolve(),
        jwt && data.createdWorkOrderDocumentId
          ? qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) })
          : Promise.resolve(),
      ]);

      setSuccessWasDateOnly(Boolean(data?.isDateOnlySave));
      setSuccessCreatedWorkOrder(Boolean(data?.createdWorkOrderDocumentId));
      const needsTeamApproval =
        !data?.isDateOnlySave &&
        Boolean(data?.createdWorkOrderDocumentId || scheduleDetail?.work_order?.documentId) &&
        !String(scheduleDetail?.work_order?.job_number ?? '').trim() &&
        (isSamplingAdmin || isSuper);
      setSuccessNeedsTeamApproval(needsTeamApproval);
      setRedirectToListAfterSuccess(!variables.stayOnPage && !needsTeamApproval);
      setTeamSaveTick((n) => n + 1);
      // Snackbar setelah data kalender/tim sudah di-refetch agar UI sync dengan DB.
      setTeamSaveSuccessOpen(true);
    },
  });

  const removeScheduleDateMut = useMutation({
    mutationFn: async ({
      date,
      targetScheduleDocumentId,
      notes,
      transportasiNote,
    }: {
      date: string;
      targetScheduleDocumentId?: string;
      notes: string;
      transportasiNote?: string;
    }) => {
      if (!jwt || !documentId) throw new Error('Sesi tidak valid.');
      const targetDocumentId = String(targetScheduleDocumentId ?? documentId).trim() || documentId;
      const switchingSchedule = targetDocumentId !== documentId;
      const targetRow =
        schedulesForCalendar.find((row) => row.documentId === targetDocumentId) ??
        (switchingSchedule ? null : schedule);
      const targetWoDocId = String(
        targetRow?.work_order?.documentId ??
          (!switchingSchedule ? schedule?.work_order?.documentId : '') ??
          '',
      ).trim();
      const targetWoLinked = Boolean(targetWoDocId);

      const result = await strapiRemoveScheduledDateFromSurveyTeams({
        jwt,
        scheduleDocumentId: targetDocumentId,
        date,
        workOrderDocumentId: targetWoDocId || null,
      });

      // Pertahankan tanggal kosong eksplisit; jangan ganti seluruh array hanya dari sisa slot tim.
      const previousAgreed = customerAgreedCalendarDatesFromSchedule(targetRow ?? schedule);
      const remaining = [
        ...new Set([
          ...previousAgreed.filter((d) => d !== date),
          ...result.remainingDates.filter((d) => d !== date),
        ]),
      ].sort();
      const customerDates = buildCustomerAgreedDatePayload(remaining);
      await strapiUpdateSamplingSchedule(jwt, targetDocumentId, {
        ...(targetWoLinked ? {} : { progress_status: 'scheduling_in_progress' }),
        notes: notes.trim() || null,
        ...(canEditScheduleTransportasiNote(user)
          ? { transportasi_note: (transportasiNote ?? '').trim() || null }
          : {}),
        customer_agreed_dates: customerDates.customer_agreed_dates,
        customer_agreed_start_date: customerDates.customer_agreed_start_date,
        customer_agreed_end_date: customerDates.customer_agreed_end_date,
      });
      if (targetWoLinked && targetWoDocId) {
        await strapiUpdateWorkOrder(jwt, targetWoDocId, {
          testing_start_date: customerDates.customer_agreed_start_date,
          testing_end_date: customerDates.customer_agreed_end_date,
        });
      }

      return { targetDocumentId, remainingDates: remaining };
    },
    onSuccess: async (data) => {
      await Promise.all([
        teamsQ.refetch(),
        qc.refetchQueries({ queryKey: queryKeys.surveyTeamsSchedule(jwt, null) }),
        qc.refetchQueries({ queryKey: ['strapi', 'samplingSchedule', jwt, documentId] }),
        qc.refetchQueries({
          queryKey: ['strapi', 'samplingSchedulesForCalendarOverview', jwt],
        }),
        data.targetDocumentId !== documentId
          ? qc.refetchQueries({
              queryKey: ['strapi', 'samplingSchedule', jwt, data.targetDocumentId],
            })
          : Promise.resolve(),
      ]);
      setSuccessWasDateOnly(false);
      setSuccessCreatedWorkOrder(false);
      setSuccessNeedsTeamApproval(false);
      setRedirectToListAfterSuccess(false);
      setTeamSaveTick((n) => n + 1);
      setTeamSaveSuccessOpen(true);
    },
  });

  const approveTeamMut = useMutation({
    mutationFn: async () => {
      if (!jwt || !documentId) throw new Error('Sesi tidak valid.');
      return strapiApproveSamplingSchedule(jwt, documentId);
    },
    onSuccess: () => {
      void teamsQ.refetch();
      void qc.invalidateQueries({ queryKey: ['strapi', 'samplingSchedule', jwt, documentId] });
      void qc.invalidateQueries({ queryKey: ['strapi', 'surveyTeamsSchedule', jwt] });
      void qc.invalidateQueries({ queryKey: ['strapi', 'samplingSchedulesForCalendarOverview', jwt] });
      if (jwt && woDocId) {
        void qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, woDocId) });
        void qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
      }
    },
  });

  function closeTeamSaveSuccessDialog() {
    setTeamSaveSuccessOpen(false);
    const shouldRedirect = redirectToListAfterSuccess;
    setRedirectToListAfterSuccess(false);
    setSuccessWasDateOnly(false);
    setSuccessCreatedWorkOrder(false);
    setSuccessNeedsTeamApproval(false);
    if (shouldRedirect) {
      navigate('/sampling/schedules');
    }
  }

  if (!documentId) {
    return <Alert severity="warning">ID penjadwalan tidak valid.</Alert>;
  }

  if (menuQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  if (detailQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (detailQ.isError || !schedule) {
    return (
      <Alert severity="error">
        {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat penjadwalan.'}
      </Alert>
    );
  }

  const label = schedule.schedule_number ?? schedule.documentId;
  const customer = schedule.quotation?.customer as
    | (NonNullable<typeof schedule.quotation>['customer'] & {
        address?: string | null;
        city?: string | null;
        province?: string | null;
      })
    | null
    | undefined;
  const customerName =
    schedule.quotation_group?.customer?.name?.trim() ||
    customer?.name?.trim() ||
    '';
  const customerAddress = [customer?.address, customer?.city, customer?.province]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
    .join(', ');
  const workOrderJobNumber = String(schedule.work_order?.job_number ?? '').trim();
  const canApproveSurveyTeam = canApproveSurveyTeamForSampling(
    schedule,
    teamsQ.data ?? [],
    user,
  );
  const waitingForSamplingApproval =
    Boolean(woDocId) &&
    !workOrderJobNumber &&
    !isPointReschedule &&
    (isSamplingAdmin || isSuper);

  return (
    <PageShell
      fill
      title={`Penjadwalan — ${label}`}
      subtitle={
        <Typography variant="body2" color="text.secondary" data-testid="schedule-detail-meta">
          <Box component="span" data-testid="schedule-detail-customer" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {customerName || '—'}
          </Box>
          {' · '}
          Quotation: {schedule.quotation?.quotation_number ?? '—'} · Status: {status}
          {woDocId ? ` · WO: ${workOrderJobNumber || woDocId}` : ''}
          {isPointReschedule ? ` · Reschedule titik ${schedule.reschedule_point_index ?? '—'}` : ''}
        </Typography>
      }
      actions={
        <>
          {canApproveSurveyTeam ? (
            <Button
              size="small"
              variant="contained"
              data-testid="schedule-detail-approve-survey-team"
              disabled={approveTeamMut.isPending}
              onClick={() => {
                setActionError(null);
                approveTeamMut.mutate(undefined, {
                  onError: (e) =>
                    setActionError(
                      e instanceof Error ? e.message : 'Gagal menyetujui tim survey.',
                    ),
                });
              }}
            >
              {approveTeamMut.isPending ? 'Menyetujui…' : 'Setujui tim survey'}
            </Button>
          ) : null}
          {woDocId ? (
            <Button
              size="small"
              component={RouterLink}
              to={`/sampling/work-order/${encodeURIComponent(woDocId)}/view`}
              data-testid="schedule-detail-view-work-order"
            >
              Lihat Work Order
            </Button>
          ) : null}
          <Button size="small" component={RouterLink} to="/sampling/schedules">
            Daftar
          </Button>
        </>
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>

      {isPointReschedule ? (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="schedule-detail-reschedule-banner">
          Jadwal reschedule titik dari petugas sampling. Admin sampling menentukan ulang tanggal final
          dan menugaskan petugas yang siap. Titik lain pada work order tetap memakai jadwal lama.
          {schedule.petugas_proposed_at
            ? ` Usulan petugas: ${customerAgreedCalendarDatesFromSchedule(schedule)[0] ?? '—'}.`
            : ''}
        </Alert>
      ) : null}

      {waitingForSamplingApproval ? (
        <Alert
          severity={canApproveSurveyTeam ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          data-testid="schedule-detail-awaiting-sampling-approval"
        >
          {canApproveSurveyTeam
            ? 'Tim survey siap. Setujui tim agar nomor job terbit dan petugas sampling dapat memulai di menu Sampling.'
            : 'Work order sudah dibuat. Nomor job terbit setelah admin sampling menugaskan minimal 2 petugas lalu menyetujui tim survey.'}
        </Alert>
      ) : null}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <SamplingSchedulePlanningTab
        jwt={jwt}
        scheduleDocumentId={documentId}
        scheduleNumber={schedule.schedule_number}
        scheduleStatus={status}
        adminWorkorderCompletedAt={schedule.admin_workorder_completed_at}
        adminSamplingCompletedAt={schedule.admin_sampling_completed_at}
        samplingReceivedAt={schedule.sampling_received_at}
        quotationDocumentId={schedule.quotation?.documentId}
        quotationNumber={schedule.quotation?.quotation_number}
        quotationUsesGroups={usesGroups}
        quotationGroupMeta={
          schedule.quotation
            ? {
                is_contract: schedule.quotation.is_contract,
                is_consultant_quote: schedule.quotation.is_consultant_quote,
                contract_period_start_date: schedule.quotation.contract_period_start_date,
                contract_period_end_date: schedule.quotation.contract_period_end_date,
              }
            : undefined
        }
        scheduleQuotationGroup={schedule.quotation_group}
        recurringEventIndex={schedule.quotation_recurring_event?.event_index ?? null}
        recurringEventDocumentId={schedule.quotation_recurring_event?.documentId ?? null}
        customerName={customerName || undefined}
        customerAddress={customerAddress || undefined}
        scheduleNotes={schedule.notes}
        scheduleTransportasiNote={schedule.transportasi_note}
        workOrderDocumentId={woDocId || null}
        workOrderJobNumber={workOrderJobNumber || null}
        anchorDate={teamAnchorDate}
        anchorEndDate={teamAnchorEndDate}
        customerAgreedDates={teamAgreedDates}
        locked={locked}
        editMode={editMode}
        teams={teamsQ.data ?? []}
        allSchedulesForCalendar={schedulesForCalendar}
        users={usersQ.data?.rows ?? []}
        usersLoading={usersQ.isPending}
        saving={saveTeamMut.isPending || removeScheduleDateMut.isPending}
        teamSaveTick={teamSaveTick}
        onRemoveScheduleDate={async ({ date, scheduleDocumentId, notes, transportasiNote }) => {
          setActionError(null);
          try {
            await removeScheduleDateMut.mutateAsync({
              date,
              targetScheduleDocumentId: scheduleDocumentId,
              notes,
              transportasiNote,
            });
          } catch (e) {
            setActionError(e instanceof Error ? e.message : 'Gagal menghapus tanggal jadwal.');
            throw e;
          }
        }}
        onSave={(
          members,
          slotsByUserId,
          notes,
          planDocumentIds,
          dateOnlySlots,
          transportasiNote,
          options,
        ) => {
          setActionError(null);
          saveTeamMut.mutate(
            {
              members,
              slotsByUserId,
              notes,
              planDocumentIds,
              dateOnlySlots,
              transportasiNote,
              stayOnPage: options?.stayOnPage,
              allowEmptyTeam: options?.allowEmptyTeam,
              quotationGroupDocumentId: options?.quotationGroupDocumentId,
              quotationRecurringEventDocumentId: options?.quotationRecurringEventDocumentId,
              targetScheduleDocumentId: options?.targetScheduleDocumentId,
              workOrderCreate: options?.workOrderCreate,
              teamNamesByDate: options?.teamNamesByDate,
              teamName: options?.teamName,
              propagateRosterToScheduleDocumentIds: options?.propagateRosterToScheduleDocumentIds,
              replaceRosterOnDate: options?.replaceRosterOnDate,
              appendRoster: options?.appendRoster,
            },
            {
              onError: (e) => setActionError(e instanceof Error ? e.message : 'Gagal menyimpan tim.'),
            },
          );
        }}
      />
      </Box>

      <Snackbar
        open={teamSaveSuccessOpen}
        autoHideDuration={redirectToListAfterSuccess ? 2500 : 4000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          closeTeamSaveSuccessDialog();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="schedule-team-save-success-dialog"
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={closeTeamSaveSuccessDialog}
          action={
            <Button color="inherit" size="small" onClick={closeTeamSaveSuccessDialog}>
              OK
            </Button>
          }
          sx={{ alignItems: 'center', maxWidth: 480 }}
        >
          {successCreatedWorkOrder
            ? 'Penjadwalan dan work order tersimpan. Nomor job belum terbit — admin sampling menugaskan petugas lalu menyetujui tim survey.'
            : successNeedsTeamApproval
              ? 'Tim survey tersimpan. Setujui tim survey agar nomor job terbit dan petugas dapat memulai sampling.'
              : successWasDateOnly
                ? 'Tanggal penjadwalan tersimpan.'
                : 'Perubahan penjadwalan berhasil disimpan.'}
        </Alert>
      </Snackbar>
    </PageShell>
  );
}
